import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, Route, Routes, useParams } from 'react-router-dom';
import { api } from '../api';
import { fehlendeFunktion, upgradeAnbieten } from '../billtoro/upgrade';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { isBilltoro } from '../produkt';
import { KontakteNav } from './kontakte/KontakteNav';
import { dateienZipUrl, gleichesWort, passtZuKontakt } from './kontakte/KontaktDaten';
import { MitVorschlaegen, SchlagwoerterDialog, SchlagwortChips, SchlagwortFilter } from './kontakte/Schlagwoerter';
import { KundeDetail } from './KundeDetail';
import { ART_NAME, kundenUrl, ortOf, type KundenAntwort, type KundeVoll, type KundeZeile } from './KundeDaten';
import { KundeForm } from './KundeForm';
import { KundenImport } from './KundenImport';
import { darf, fehlerText, GesperrtKnopf, MitRecht, NurAnsehen } from './tarifHinweis';
import type { Overview } from './types';
import { urlId } from './urlId';
import './kunden.css';

type Filter = 'alle' | 'offen' | 'ueberfaellig';
const FILTERS: [Filter, string][] = [
  ['alle', 'Alle'],
  ['offen', 'Mit Offenem'],
  ['ueberfaellig', 'Überfällig'],
];
type Sort = 'name' | 'nummer' | 'umsatz' | 'offen' | 'letzte';
const SORTS: [Sort, string][] = [
  ['name', 'Name'],
  ['nummer', 'Kundennummer'],
  ['umsatz', 'Umsatz'],
  ['offen', 'Offen'],
  ['letzte', 'Letzte Rechnung'],
];
// so viele Karten auf einmal (nach einem Import können es Tausende sein)
const SEITE = 60;
// Billtoro nennt beim Import kein anderes Programm beim Namen
const VON_WO = isBilltoro() ? 'Ihrem bisherigen Rechnungsprogramm' : 'Lexware, sevDesk';

export interface ListeZustand {
  q: string;
  filter: Filter;
  // Schlagwort ('' = alle)
  schlagwort: string;
  sort: Sort;
}
const START: ListeZustand = { q: '', filter: 'alle', schlagwort: '', sort: 'name' };

// Kundenliste (Funktionsliste Punkt 2): /rechnung/kunden (Liste), /neu, /import, /:id (ein Kunde), /:id/bearbeiten.
// Suche, Filter, Schlagwort und Sortierung bleiben beim Zurückkommen aus einem Kunden erhalten. Billtoro: oben die Chips
// „Kunden“ · „Lieferanten“ (kontakte/KontakteNav.tsx).
export function KundenList({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const [liste, setListe] = useState<KundenAntwort | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zustand, setZustand] = useState<ListeZustand>(START);
  const firmId = data.firm.id;
  const aktuell = useRef(firmId);
  aktuell.current = firmId;

  const laden = useCallback(async () => {
    try {
      const res = await api.get<KundenAntwort>(kundenUrl(firmId));
      // inzwischen andere Firma gewählt: Antwort verwerfen
      if (aktuell.current !== firmId) return;
      setListe(res);
      setError(null);
    } catch (err) {
      if (aktuell.current === firmId) setError(fehlerText(err));
    }
  }, [firmId]);

  // andere Firma gewählt: Liste und Auswahl neu (ihre Schlagwörter sind andere)
  useEffect(() => {
    setListe(null);
    setZustand(START);
  }, [firmId]);

  // nach einer Änderung: Liste und Übersicht (Kunden im Rechnungsformular) neu
  const geaendert = useCallback(async () => {
    await Promise.all([laden(), reload()]);
  }, [laden, reload]);

  // Vorschläge für das Feld „Schlagwörter“ (Schlagwörter der Firma aus der Liste)
  const vorschlaege = liste?.schlagwoerter.map((w) => w.wort) ?? [];

  // Billtoro: Formulare und Import nur mit dem Recht „Kontakte, Leistungen und Lager“, sonst zurück zur Liste mit Hinweis
  const recht = (form: ReactNode) => (
    <MitRecht tarif={data.tarif} bereich="kunden" zu="/rechnung/kunden">
      <MitVorschlaegen da={!!liste} laden={laden}>
        {form}
      </MitVorschlaegen>
    </MitRecht>
  );
  return (
    <Routes>
      <Route
        index
        element={
          <Liste data={data} liste={liste} error={error} laden={laden} geaendert={geaendert} zustand={zustand} setZustand={setZustand} />
        }
      />
      <Route path="neu" element={recht(<KundeForm data={data} naechste={liste?.naechsteNummer ?? null} vorschlaege={vorschlaege} onSaved={geaendert} />)} />
      <Route path="import" element={recht(<KundenImport data={data} onDone={geaendert} />)} />
      <Route path=":id" element={<NurMitId><KundeDetail data={data} onChanged={geaendert} /></NurMitId>} />
      <Route path=":id/bearbeiten" element={recht(<NurMitId><Bearbeiten data={data} vorschlaege={vorschlaege} onSaved={geaendert} /></NurMitId>)} />
      <Route path="*" element={<Navigate to="/rechnung/kunden" replace />} />
    </Routes>
  );
}

// Kunde aus der Adresse geht in API-Pfade: nur mit echter ID, sonst zur Liste (urlId.ts)
// „Importieren“: Billtoro-Tarif ohne Kundenimport: Marke „Start“ und das Fenster „Mehr mit Billtoro“ statt der Seite.
// to: von der Lieferantenliste aus die Seite der Kunden (/rechnung/kunden/import), mit von: 'lieferanten' für den Rückweg
export function ImportKnopf({ data, to = 'import', von, children }: { data: Overview; to?: string; von?: 'lieferanten'; children: ReactNode }) {
  const fehlt = fehlendeFunktion(data.tarif, 'kundenimport');
  if (fehlt)
    return (
      <GesperrtKnopf tarif={data.tarif} fehlt={fehlt} className="btn" onUpgrade={() => upgradeAnbieten(data.tarif, 'kundenimport')}>
        {children}
      </GesperrtKnopf>
    );
  return (
    <Link to={to} state={von ? { von } : undefined} className="btn">
      {children}
    </Link>
  );
}

function NurMitId({ children }: { children: ReactNode }) {
  const { id } = useParams();
  return urlId(id) ? children : <Navigate to="/rechnung/kunden" replace />;
}

function Bearbeiten({ data, vorschlaege, onSaved }: { data: Overview; vorschlaege: string[]; onSaved(): Promise<void> }) {
  const { id } = useParams();
  return <KundeForm key={id} data={data} id={id} naechste={null} vorschlaege={vorschlaege} onSaved={onSaved} />;
}

// Suche: Name (auch Zusatz), Kundennummer, PLZ und Ort, E-Mail, Schlagwörter, Namen und E-Mails der Ansprechpartner
function passt(k: KundeZeile, q: string) {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  if (/^\d+$/.test(t) && String(k.nummer).startsWith(t)) return true;
  return [k.name, k.extra, `${k.zip} ${k.city}`, k.email, k.emailCc].some((v) => v?.toLowerCase().includes(t)) || passtZuKontakt(k, t);
}

function sortiert(list: KundeVoll[], sort: Sort) {
  const name = (a: KundeVoll, b: KundeVoll) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }) || a.nummer - b.nummer;
  const f: Record<Sort, (a: KundeVoll, b: KundeVoll) => number> = {
    name,
    nummer: (a, b) => a.nummer - b.nummer,
    umsatz: (a, b) => b.umsatzJahr - a.umsatzJahr || b.umsatzGesamt - a.umsatzGesamt || name(a, b),
    offen: (a, b) => b.ueberfaellig - a.ueberfaellig || b.offen - a.offen || name(a, b),
    letzte: (a, b) => (b.letzteRechnung?.invoiceDate ?? '').localeCompare(a.letzteRechnung?.invoiceDate ?? '') || name(a, b),
  };
  return [...list].sort(f[sort]);
}

const summe = (list: KundeVoll[], f: (k: KundeVoll) => number) => Math.round(list.reduce((s, k) => s + f(k), 0) * 100) / 100;
const kunden = (n: number) => `${n} ${n === 1 ? 'Kunde' : 'Kunden'}`;

function Liste({
  data,
  liste,
  error,
  laden,
  geaendert,
  zustand,
  setZustand,
}: {
  data: Overview;
  liste: KundenAntwort | null;
  error: string | null;
  laden(): Promise<void>;
  geaendert(): Promise<void>;
  zustand: ListeZustand;
  setZustand(z: ListeZustand): void;
}) {
  const [mehr, setMehr] = useState(SEITE);
  const [verwalten, setVerwalten] = useState(false);
  const { q, filter, schlagwort, sort } = zustand;
  // Billtoro: Rechte des Logins (nur Anzeige, der Server prüft)
  const pflegen = darf(data.tarif, 'kunden');
  const exportieren = darf(data.tarif, 'export');
  // Billtoro: alle Dateien an Kunden und Lieferanten als ZIP (neben den Chips)
  const zip = exportieren ? dateienZipUrl(data.firm.id) : undefined;
  const set = (z: Partial<ListeZustand>) => {
    setZustand({ ...zustand, ...z });
    setMehr(SEITE);
  };

  // bei jedem Besuch der Liste neu laden (Zahlen ändern sich mit jeder Rechnung und Zahlung)
  useEffect(() => {
    void laden();
  }, [laden]);

  if (!liste)
    return (
      <>
        <KontakteNav zip={zip} />
        {error ? <div className="notice bad">{error}</div> : <div className="spinner" aria-label="Lädt" />}
      </>
    );
  const alle = liste.kunden;
  const offen = alle.filter((k) => k.offenAnzahl > 0);
  const ueber = alle.filter((k) => k.ueberfaelligAnzahl > 0);
  const shown = sortiert(
    alle.filter(
      (k) =>
        (filter === 'offen' ? k.offenAnzahl > 0 : filter === 'ueberfaellig' ? k.ueberfaelligAnzahl > 0 : true) &&
        (!schlagwort || k.schlagwoerter.some((w) => gleichesWort(w, schlagwort))) &&
        passt(k, q),
    ),
    sort,
  );

  return (
    <>
      <KontakteNav zip={zip} />
      <div className="rt-head kd-head">
        <div>
          <h1>
            Kunden <NurAnsehen tarif={data.tarif} bereich="kunden" />
          </h1>
          <p className="muted">{alle.length ? kunden(alle.length) : 'Noch keine Kunden'}</p>
        </div>
        {alle.length > 0 && (pflegen || exportieren) && (
          <div className="rt-head-act">
            {pflegen && <ImportKnopf data={data}>Importieren</ImportKnopf>}
            {exportieren && (
              <a className="btn" href={`${kundenUrl(data.firm.id)}/export`} download>
                Exportieren
              </a>
            )}
            {pflegen && (
              <Link to="neu" className="btn primary kd-neu">
                + Neuer Kunde
              </Link>
            )}
          </div>
        )}
      </div>

      {alle.length === 0 ? (
        <div className="rt-card rt-empty kd-empty">
          <p>
            Legen Sie Ihren ersten Kunden an oder übernehmen Sie Ihre Kunden aus {VON_WO} oder einer Excel-Liste. Kunden aus neuen
            Rechnungen kommen von selbst hierher.
          </p>
          {pflegen && (
            <div className="kd-empty-act">
              <ImportKnopf data={data}>Kunden importieren</ImportKnopf>
              <Link to="neu" className="btn primary">
                + Neuer Kunde
              </Link>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="rt-kpis">
            <div className="rt-kpi kd-kpi-fix">
              <span className="rt-kpi-l">Umsatz {liste.jahr}</span>
              <span className="rt-kpi-v num">{eurC(summe(alle, (k) => k.umsatzJahr))}</span>
              <span className="rt-kpi-s">
                netto, {kunden(alle.filter((k) => k.umsatzJahr !== 0).length)}
                {umgerechnet(alle)}
              </span>
            </div>
            <button type="button" className={`rt-kpi${filter === 'offen' ? ' on' : ''}`} onClick={() => set({ filter: filter === 'offen' ? 'alle' : 'offen' })}>
              <span className="rt-kpi-l">Offen</span>
              <span className="rt-kpi-v num">{eurC(summe(offen, (k) => k.offen))}</span>
              <span className="rt-kpi-s">
                bei {kunden(offen.length)}
                {umgerechnet(offen)}
              </span>
            </button>
            <button
              type="button"
              className={`rt-kpi${ueber.length ? ' bad' : ''}${filter === 'ueberfaellig' ? ' on' : ''}`}
              onClick={() => set({ filter: filter === 'ueberfaellig' ? 'alle' : 'ueberfaellig' })}
            >
              <span className="rt-kpi-l">Überfällig</span>
              <span className="rt-kpi-v num">{eurC(summe(ueber, (k) => k.ueberfaellig))}</span>
              <span className="rt-kpi-s">
                {ueber.length ? `bei ${kunden(ueber.length)}` : 'nichts überfällig'}
                {umgerechnet(ueber)}
              </span>
            </button>
          </div>

          <div className="rt-tools kd-tools">
            <input
              type="search"
              className="rt-search"
              value={q}
              placeholder="Suchen: Name, Nummer, Ort, E-Mail oder Ansprechpartner"
              aria-label="Kunden suchen"
              onChange={(e) => set({ q: e.target.value })}
            />
            <div className="rt-chips" role="group" aria-label="Filter">
              {FILTERS.map(([f, label]) => (
                <button key={f} type="button" className={`rt-chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => set({ filter: f })}>
                  {label}
                </button>
              ))}
            </div>
            <SchlagwortFilter woerter={liste.schlagwoerter} art="kunde" value={schlagwort} onChange={(v) => set({ schlagwort: v })} />
            {pflegen && liste.schlagwoerter.length > 0 && (
              <button type="button" className="linkbtn kt-verwalten" onClick={() => setVerwalten(true)}>
                Schlagwörter verwalten
              </button>
            )}
            <label className="kd-sort">
              <span>Sortieren</span>
              <select value={sort} onChange={(e) => set({ sort: e.target.value as Sort })}>
                {SORTS.map(([s, label]) => (
                  <option key={s} value={s}>
                    {label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {shown.length === 0 ? (
            <div className="rt-card rt-empty">
              <p>Kein Kunde passt zu dieser Auswahl.</p>
              <button type="button" className="btn small" onClick={() => set({ q: '', filter: 'alle', schlagwort: '' })}>
                Alle zeigen
              </button>
            </div>
          ) : (
            <>
              <ul className="kd-grid">
                {shown.slice(0, mehr).map((k) => (
                  <li key={k.id}>
                    <Karte k={k} jahr={liste.jahr} />
                  </li>
                ))}
              </ul>
              {shown.length > mehr && (
                <p className="kd-mehr">
                  <button type="button" className="btn" onClick={() => setMehr(mehr + SEITE * 4)}>
                    Weitere zeigen ({shown.length - mehr})
                  </button>
                </p>
              )}
            </>
          )}
        </>
      )}
      <SchlagwoerterDialog
        open={verwalten}
        firmId={data.firm.id}
        woerter={liste.schlagwoerter}
        onClose={() => setVerwalten(false)}
        onErsetzt={(alt, neu) => schlagwort && gleichesWort(alt, schlagwort) && set({ schlagwort: neu ?? '' })}
        onChanged={geaendert}
      />
    </>
  );
}

// Zusatz in den Kennzahlen, wenn Belege in anderer Währung darin stecken (Beträge vom Server in Euro umgerechnet)
const umgerechnet = (liste: KundeVoll[]) => (liste.some((k) => k.fremd) ? ' · umgerechnet zum Kurs der Belege' : '');

function Karte({ k, jahr }: { k: KundeVoll; jahr: number }) {
  return (
    <Link to={k.id} className={`kd-card${k.warnung === 'ueberfaellig' ? ' bad' : ''}`}>
      <span className="kd-top">
        <b className="kd-name">{k.name}</b>
        <span className="kd-nr num" aria-label={`Kundennummer ${k.nummer}`}>
          {k.nummer}
        </span>
      </span>
      <span className="kd-ort">
        <span>
          {ortOf(k)}
          {k.art !== 'firma' ? ` · ${ART_NAME[k.art]}` : ''}
        </span>
        {k.waehrung !== 'EUR' && <span className="badge b-none">{k.waehrung}</span>}
        {k.warnung === 'ueberfaellig' ? (
          <span className="badge b-bad">Überfällig</span>
        ) : (
          k.warnung === 'langsam' && <span className="badge b-warn">Zahlt langsam</span>
        )}
      </span>
      <span className="kd-zahlen">
        <span>
          <small>Umsatz {jahr}</small>
          <b title={k.fremd ? 'umgerechnet zum Kurs der Belege' : undefined}>{eurC(k.umsatzJahr)}</b>
        </span>
        <span>
          <small>Offen</small>
          <b className={k.ueberfaellig > 0 ? 'kd-bad' : undefined} title={k.fremd && k.offen > 0 ? 'umgerechnet zum Kurs der Belege' : undefined}>
            {k.offen > 0 ? eurC(k.offen) : '–'}
          </b>
        </span>
        <span>
          <small>Letzte Rechnung</small>
          <span className="kd-date">{k.letzteRechnung ? fmtDate(k.letzteRechnung.invoiceDate) : '–'}</span>
        </span>
      </span>
      <SchlagwortChips woerter={k.schlagwoerter} />
    </Link>
  );
}
